import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Upload, Users, Film, Trash2, Loader2, ArrowLeft, Phone, Mail, CalendarDays, IdCard, Eye, CheckCircle2, AlertTriangle, CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, adminHeaders } from "@/lib/api";

export default function Admin() {
  const [passcode, setPasscode] = useState(sessionStorage.getItem("orl_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (passcode) verify(passcode, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (code, silent = false) => {
    setChecking(true);
    try {
      await api.post("/admin/verify", {}, adminHeaders(code));
      sessionStorage.setItem("orl_admin", code);
      setAuthed(true);
    } catch {
      if (!silent) toast.error("Incorrect passcode");
      sessionStorage.removeItem("orl_admin");
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl" data-testid="admin-gate">
          <div className="w-14 h-14 rounded-2xl bg-navy flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-gold" />
          </div>
          <h1 className="mt-5 font-heading font-bold text-2xl text-navy text-center">Owner Login</h1>
          <p className="mt-1 text-center text-muted-foreground text-sm">Enter your passcode to manage bookings, licenses, videos &amp; leads.</p>
          <form
            onSubmit={(e) => { e.preventDefault(); verify(passcode); }}
            className="mt-6 space-y-4"
          >
            <Input
              type="password"
              data-testid="admin-passcode-input"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="h-12 border-navy/20 focus-visible:ring-navy"
            />
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={checking}
              className="w-full h-12 rounded-full bg-gold text-navy font-bold hover:bg-gold-dark"
            >
              {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unlock"}
            </Button>
          </form>
          <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-navy">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
        </div>
      </div>
    );
  }

  return <Dashboard passcode={passcode} onLogout={() => { sessionStorage.removeItem("orl_admin"); setAuthed(false); setPasscode(""); }} />;
}

function Dashboard({ passcode, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/bookings", adminHeaders(passcode)).then((r) => setBookings(r.data)).catch(() => {}),
      api.get("/licenses", adminHeaders(passcode)).then((r) => setLicenses(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [passcode]);

  return (
    <div className="min-h-screen bg-secondary">
      <header className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-heading font-extrabold text-xl">ORL</span>
            <span className="text-gold text-sm font-semibold">Owner Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/70 hover:text-white">View Site</Link>
            <Button data-testid="admin-logout-btn" onClick={onLogout} variant="outline" className="h-9 rounded-full border-white/30 text-black hover:bg-white hover:text-navy">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <StatsRow bookings={bookings} licenses={licenses} />
        <Tabs defaultValue="bookings">
          <TabsList className="bg-white border border-navy/10 flex-wrap h-auto">
            <TabsTrigger value="bookings" data-testid="tab-bookings"><CalendarDays className="w-4 h-4 mr-2" /> Bookings</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarRange className="w-4 h-4 mr-2" /> Calendar</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="tab-licenses"><IdCard className="w-4 h-4 mr-2" /> Licenses</TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload"><Upload className="w-4 h-4 mr-2" /> Upload Video</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos"><Film className="w-4 h-4 mr-2" /> Videos</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads"><Users className="w-4 h-4 mr-2" /> Leads</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6"><BookingsTab passcode={passcode} bookings={bookings} setBookings={setBookings} loading={loading} /></TabsContent>
          <TabsContent value="calendar" className="mt-6"><CalendarTab bookings={bookings} loading={loading} /></TabsContent>
          <TabsContent value="licenses" className="mt-6"><LicensesTab passcode={passcode} licenses={licenses} loading={loading} /></TabsContent>
          <TabsContent value="upload" className="mt-6"><UploadTab passcode={passcode} /></TabsContent>
          <TabsContent value="videos" className="mt-6"><VideosTab passcode={passcode} /></TabsContent>
          <TabsContent value="leads" className="mt-6"><LeadsTab passcode={passcode} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatsRow({ bookings, licenses }) {
  const now = new Date();
  const active = bookings.filter((b) => (b.status || "New") !== "Cancelled");
  const monthRevenue = active
    .filter((b) => {
      const d = new Date(b.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((s, b) => s + (b.total_due || 0), 0);
  const stats = [
    { label: "Total bookings", value: bookings.length, testid: "stat-total-bookings" },
    { label: "New inquiries", value: bookings.filter((b) => (b.status || "New") === "New").length, testid: "stat-new-inquiries" },
    { label: "Booked this month", value: `$${monthRevenue.toLocaleString()}`, testid: "stat-month-revenue" },
    { label: "Licenses to review", value: licenses.filter((l) => !l.verified).length, testid: "stat-licenses-review" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
      {stats.map((s) => (
        <div key={s.label} className="rounded-2xl bg-white border border-navy/10 p-5" data-testid={s.testid}>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</p>
          <p className="mt-1 font-heading font-extrabold text-2xl text-navy">{s.value}</p>
        </div>
      ))}
    </div>
  );
}

const STATUS_STYLES = {
  New: "bg-gold/15 text-gold-dark border-gold/40",
  Confirmed: "bg-green-100 text-green-700 border-green-300",
  Completed: "bg-navy/10 text-navy border-navy/30",
  Cancelled: "bg-red-50 text-red-600 border-red-200",
};

function StatusBadge({ status, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${STATUS_STYLES[status] || STATUS_STYLES.New}`}>
      {status}
    </span>
  );
}

function LicenseImage({ passcode, id, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    api.get(`/licenses/${id}/image`, { headers: { "X-Admin-Passcode": passcode }, responseType: "blob" })
      .then((r) => {
        if (alive) {
          url = URL.createObjectURL(r.data);
          setSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, passcode]);
  if (!src) return <div className={`bg-navy/5 animate-pulse ${className}`} data-testid={`license-img-loading-${id}`} />;
  return <img src={src} alt="Driver's license" className={className} data-testid={`license-img-${id}`} />;
}

function LicenseViewerDialog({ passcode, licenseId, onClose }) {
  return (
    <Dialog open={!!licenseId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="license-dialog">
        <DialogHeader>
          <DialogTitle>Driver's license photo</DialogTitle>
          <DialogDescription>Uploaded by the renter for approval. Handle with care.</DialogDescription>
        </DialogHeader>
        {licenseId && <LicenseImage passcode={passcode} id={licenseId} className="w-full rounded-xl border border-navy/10" />}
      </DialogContent>
    </Dialog>
  );
}

function BookingsTab({ passcode, bookings, setBookings, loading }) {
  const [viewLicense, setViewLicense] = useState(null);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status }, adminHeaders(passcode));
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update the booking status");
    }
  };

  if (loading) return <p className="text-muted-foreground">Loading...</p>;
  if (bookings.length === 0)
    return <div className="rounded-2xl bg-white border border-navy/10 p-10 text-center text-muted-foreground" data-testid="bookings-empty">No bookings yet. New rental inquiries from your website will appear here.</div>;

  return (
    <div className="space-y-4" data-testid="bookings-list">
      {bookings.map((b) => {
        const status = b.status || "New";
        return (
          <div key={b.id} className="rounded-2xl bg-white border border-navy/10 p-5 sm:p-6" data-testid={`booking-card-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-bold text-navy text-lg">{b.name}</h3>
                  <StatusBadge status={status} testid={`booking-status-badge-${b.id}`} />
                </div>
                <p className="text-sm text-muted-foreground mt-1.5">{b.car_name} · {b.days} days · {b.pickup_date || "—"} → {b.return_date || "—"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{new Date(b.created_at).toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total due at pickup</p>
                <p className="font-heading font-extrabold text-2xl text-navy" data-testid={`booking-total-${b.id}`}>${b.total_due}</p>
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground mb-1">Price breakdown</p>
                <p className="text-navy">Rental ${b.rental_total}</p>
                <p className="text-navy">Deposit ${b.deposit} (refundable)</p>
              </div>
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground mb-1">Details</p>
                <p className="text-navy">Drives for: {b.drives_for || "—"}</p>
              </div>
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground mb-1">Driver's license</p>
                {b.license_status ? (
                  <p className={`font-semibold flex items-center gap-1.5 ${b.license_verified ? "text-green-700" : "text-amber-600"}`}>
                    {b.license_verified ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    {b.license_status}
                  </p>
                ) : (
                  <p className="text-muted-foreground">Not provided</p>
                )}
                {b.license_name && <p className="text-navy text-xs mt-0.5">{b.license_name}{b.license_expiry ? ` · exp ${b.license_expiry}` : ""}</p>}
                {b.license_id && (
                  <button type="button" data-testid={`booking-view-license-${b.id}`} onClick={() => setViewLicense(b.license_id)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-gold-dark hover:text-navy transition-colors">
                    <Eye className="w-3.5 h-3.5" /> View license photo
                  </button>
                )}
              </div>
            </div>

            {b.message && <p className="mt-3 text-sm text-muted-foreground italic">"{b.message}"</p>}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <a href={`tel:${b.phone}`} data-testid={`booking-call-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-white text-xs font-semibold hover:bg-navy-light"><Phone className="w-3.5 h-3.5" /> {b.phone}</a>
              <a href={`sms:${b.phone}`} data-testid={`booking-text-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-navy text-xs font-semibold hover:bg-secondary"><Mail className="w-3.5 h-3.5" /> Text</a>
              <a href={`mailto:${b.email}`} data-testid={`booking-email-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-navy text-xs font-semibold hover:bg-secondary truncate max-w-[220px]"><Mail className="w-3.5 h-3.5 shrink-0" /> {b.email}</a>
              <div className="flex flex-wrap gap-2 sm:ml-auto">
                {["Confirmed", "Completed", "Cancelled", "New"].filter((s) => s !== status).map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`booking-mark-${s.toLowerCase()}-${b.id}`}
                    onClick={() => setStatus(b.id, s)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
                      s === "Cancelled"
                        ? "border-red-200 text-red-600 hover:bg-red-50"
                        : s === "Confirmed"
                          ? "border-green-300 text-green-700 hover:bg-green-50"
                          : "border-navy/20 text-navy hover:bg-secondary"
                    }`}
                  >
                    Mark {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}
      <LicenseViewerDialog passcode={passcode} licenseId={viewLicense} onClose={() => setViewLicense(null)} />
    </div>
  );
}

function LicensesTab({ passcode, licenses, loading }) {
  const [viewId, setViewId] = useState(null);

  if (loading) return <p className="text-muted-foreground">Loading...</p>;
  if (licenses.length === 0)
    return <div className="rounded-2xl bg-white border border-navy/10 p-10 text-center text-muted-foreground" data-testid="licenses-empty">No licenses yet. Photos uploaded through the booking form will appear here.</div>;

  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="licenses-list">
        {licenses.map((l) => (
          <div key={l.id} className="rounded-2xl bg-white border border-navy/10 overflow-hidden" data-testid={`license-card-${l.id}`}>
            <button type="button" data-testid={`license-thumb-${l.id}`} onClick={() => setViewId(l.id)} className="block w-full aspect-[8/5] bg-navy/5 overflow-hidden cursor-zoom-in">
              <LicenseImage passcode={passcode} id={l.id} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </button>
            <div className="p-4">
              <p className={`text-sm font-bold flex items-center gap-1.5 ${l.verified ? "text-green-700" : "text-amber-600"}`} data-testid={`license-status-${l.id}`}>
                {l.verified ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {l.status}
              </p>
              {l.extracted_name && <p className="text-sm text-navy mt-1 font-semibold">{l.extracted_name}</p>}
              {l.expiry && <p className="text-xs text-muted-foreground mt-0.5">Expires: {l.expiry}</p>}
              {l.notes && <p className="text-xs text-muted-foreground mt-1">{l.notes}</p>}
              <p className="text-xs text-muted-foreground mt-2">{new Date(l.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
      <LicenseViewerDialog passcode={passcode} licenseId={viewId} onClose={() => setViewId(null)} />
    </>
  );
}

const fmtDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function CalendarTab({ bookings, loading }) {
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  if (loading) return <p className="text-muted-foreground">Loading...</p>;

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = fmtDay(new Date());
  const active = bookings.filter((b) => b.pickup_date && (b.status || "New") !== "Cancelled");
  const bookingsOn = (dayStr) => active.filter((b) => b.pickup_date <= dayStr && (b.return_date || b.pickup_date) >= dayStr);

  const shift = (n) => setCursor(new Date(year, month + n, 1));
  const monthLabel = cursor.toLocaleString("default", { month: "long", year: "numeric" });

  return (
    <div className="rounded-2xl bg-white border border-navy/10 p-4 sm:p-6" data-testid="booking-calendar">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-bold text-xl text-navy" data-testid="cal-month-label">{monthLabel}</h2>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="cal-prev-month" onClick={() => shift(-1)} className="w-9 h-9 rounded-full border border-navy/20 flex items-center justify-center text-navy hover:bg-secondary transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" data-testid="cal-today-btn" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }} className="h-9 rounded-full border border-navy/20 px-4 text-xs font-bold text-navy hover:bg-secondary transition-colors">
            Today
          </button>
          <button type="button" data-testid="cal-next-month" onClick={() => shift(1)} className="w-9 h-9 rounded-full border border-navy/20 flex items-center justify-center text-navy hover:bg-secondary transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {active.length === 0 && (
        <p className="text-sm text-muted-foreground mb-4" data-testid="calendar-empty-note">No dated rentals yet — bookings appear here from pickup to return day as they come in.</p>
      )}

      <div className="grid grid-cols-7 gap-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] sm:text-xs font-bold text-muted-foreground text-center py-1 uppercase tracking-wider">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} className="min-h-[72px] sm:min-h-[96px] rounded-lg bg-secondary/50" />;
          const dayStr = fmtDay(day);
          const dayBookings = bookingsOn(dayStr);
          const isToday = dayStr === todayStr;
          return (
            <div key={dayStr} data-testid={`cal-day-${dayStr}`} className={`min-h-[72px] sm:min-h-[96px] rounded-lg border p-1 sm:p-1.5 ${isToday ? "border-gold bg-gold/5" : "border-navy/10 bg-white"}`}>
              <p className={`text-[10px] sm:text-xs font-bold ${isToday ? "text-gold-dark" : "text-navy/60"}`}>{day.getDate()}</p>
              <div className="mt-0.5 space-y-0.5">
                {dayBookings.map((b) => {
                  const confirmed = ["Confirmed", "Completed"].includes(b.status);
                  const isTesla = b.car === "tesla";
                  return (
                    <div
                      key={b.id}
                      data-testid={`cal-chip-${b.id}`}
                      title={`${b.name} — ${b.car_name} (${b.pickup_date} → ${b.return_date || "?"}) · ${b.status || "New"}`}
                      className={`truncate rounded px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold leading-tight ${
                        confirmed
                          ? isTesla ? "bg-navy text-white" : "bg-gold text-navy"
                          : isTesla ? "border border-navy/40 text-navy" : "border border-gold/60 text-gold-dark"
                      } ${b.pickup_date === dayStr ? "ring-1 ring-green-500" : ""}`}
                    >
                      {b.name.split(" ")[0]} · {isTesla ? "Tesla" : "Hyundai"}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground" data-testid="calendar-legend">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-navy inline-block" /> Tesla · confirmed</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gold inline-block" /> Hyundai · confirmed</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-navy/40 bg-white inline-block" /> Awaiting confirmation</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded ring-1 ring-green-500 bg-white inline-block" /> Pickup day</span>
      </div>
    </div>
  );
}

function UploadTab({ passcode }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      toast.error("Add a title and choose a video file.");
      return;
    }
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", description);
    try {
      await api.post("/videos/upload", fd, {
        headers: { "X-Admin-Passcode": passcode, "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
      });
      toast.success("Video uploaded!");
      setTitle(""); setDescription(""); setFile(null);
      document.getElementById("video-file").value = "";
    } catch {
      toast.error("Upload failed. Try a smaller file or check the passcode.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-navy/10 p-7 max-w-2xl">
      <h2 className="font-heading font-bold text-xl text-navy">Upload a process video</h2>
      <p className="text-muted-foreground text-sm mt-1">These appear in the "How It Works" section on your website.</p>
      <form onSubmit={submit} className="mt-6 space-y-5">
        <div>
          <Label className="text-navy font-semibold">Title *</Label>
          <Input data-testid="upload-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="How to rent a car in 3 steps" className="mt-1.5 h-12 border-navy/20" />
        </div>
        <div>
          <Label className="text-navy font-semibold">Description</Label>
          <Textarea data-testid="upload-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description..." className="mt-1.5 border-navy/20 resize-none" rows={3} />
        </div>
        <div>
          <Label className="text-navy font-semibold">Video File *</Label>
          <Input id="video-file" data-testid="upload-file-input" type="file" accept="video/*" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 border-navy/20 file:text-navy" />
        </div>
        {uploading && (
          <div className="w-full h-2 rounded-full bg-navy/10 overflow-hidden">
            <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        <Button type="submit" data-testid="upload-submit-btn" disabled={uploading} className="h-12 rounded-full bg-gold text-navy font-bold hover:bg-gold-dark px-8">
          {uploading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Uploading {progress}%</> : <><Upload className="w-4 h-4 mr-2" /> Upload Video</>}
        </Button>
      </form>
    </div>
  );
}

function VideosTab({ passcode }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => setVideos([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try {
      await api.delete(`/videos/${id}`, adminHeaders(passcode));
      toast.success("Video removed");
      setVideos((v) => v.filter((x) => x.id !== id));
    } catch {
      toast.error("Failed to remove video");
    }
  };

  if (loading) return <p className="text-muted-foreground">Loading...</p>;
  if (videos.length === 0) return <div className="rounded-2xl bg-white border border-navy/10 p-10 text-center text-muted-foreground" data-testid="videos-empty">No videos yet. Upload your first one!</div>;

  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="videos-list">
      {videos.map((v) => (
        <div key={v.id} className="rounded-2xl bg-white border border-navy/10 overflow-hidden">
          <video src={`${api.defaults.baseURL}/videos/${v.id}/stream`} controls className="w-full aspect-video bg-black" />
          <div className="p-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading font-bold text-navy">{v.title}</h3>
              {v.description && <p className="text-sm text-muted-foreground mt-0.5">{v.description}</p>}
            </div>
            <Button data-testid={`delete-video-${v.id}`} onClick={() => remove(v.id)} variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 shrink-0">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadsTab({ passcode }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leads", adminHeaders(passcode)).then((r) => setLeads(r.data)).catch(() => setLeads([])).finally(() => setLoading(false));
  }, [passcode]);

  if (loading) return <p className="text-muted-foreground">Loading...</p>;
  if (leads.length === 0) return <div className="rounded-2xl bg-white border border-navy/10 p-10 text-center text-muted-foreground" data-testid="leads-empty">No leads yet. Inquiries from your website will appear here.</div>;

  return (
    <div className="space-y-3" data-testid="leads-list">
      {leads.map((l) => (
        <div key={l.id} className="rounded-2xl bg-white border border-navy/10 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold text-navy">{l.name} {l.platform && <span className="text-gold text-sm font-semibold">· {l.platform}</span>}</h3>
            {l.message && <p className="text-sm text-muted-foreground mt-1">{l.message}</p>}
            <p className="text-xs text-muted-foreground mt-1">{new Date(l.created_at).toLocaleString()}</p>
          </div>
          <div className="flex gap-2">
            <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-white text-sm font-semibold hover:bg-navy-light"><Phone className="w-4 h-4" /> {l.phone}</a>
            <a href={`sms:${l.phone}`} className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-4 py-2 text-navy text-sm font-semibold hover:bg-secondary"><Mail className="w-4 h-4" /> Text</a>
          </div>
        </div>
      ))}
    </div>
  );
}
